import React, { useState, useCallback, useRef } from 'react'
import { User, Loader2 } from 'lucide-react'
import { handleValidationErrors, formatPhone } from '@/shared/utils'
import { validateRobloxIntakeContact } from '../validation/robloxIntakeContactValidation'
import type { RobloxIntakeContactProps, ValidationErrors, GamerTagResult } from '@/features/enrollment'
import { US_STATES } from '@/features/enrollment/core/utils/usStates'
import { GamerTagService } from '@/features/enrollment/services/gamerTag.service'
import ValidationErrorDisplay from '../../shared/ValidationErrorDisplay'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'

export default function Step0_IntakeContact({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  handleNavigateWithCompletion,
  sessionId,
  isNavigating,
  isCompleting,
  onCampaignSelect,
  currentStepIndex,
  totalSteps = 10,
}: RobloxIntakeContactProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [reportedProblems, setReportedProblems] = useState<Record<string, boolean>>({})
  const [legalGuardianConfirmed, setLegalGuardianConfirmed] = useState<boolean>(false)
  const [robloxGamerTagResult, setRobloxGamerTagResult] = useState<GamerTagResult | null>(null)
  const [isCheckingRobloxTag, setIsCheckingRobloxTag] = useState<boolean>(false)
  const [robloxGamerTag, setRobloxGamerTag] = useState<string>('')
  
  // ✅ Use ref to track previous relationToVictim to only run effect when it changes
  const prevRelationRef = useRef(enrollmentData.relationToVictim)

  // ✅ Auto-populate fields ONLY when relationToVictim changes (not on every keystroke)
  React.useEffect(() => {
    // Only run when relationToVictim actually changes, not when names change
    if (prevRelationRef.current !== enrollmentData.relationToVictim) {
      prevRelationRef.current = enrollmentData.relationToVictim
      
      // Only auto-populate if we have the required fields
      if (enrollmentData.relationToVictim && enrollmentData.intakeFirstName && enrollmentData.intakeLastName) {
        if (enrollmentData.relationToVictim === 'Myself') {
          // "Myself" - populate injured party with same name and email
          updateEnrollmentData('injuredPartyFirstName', enrollmentData.intakeFirstName)
          updateEnrollmentData('injuredPartyLastName', enrollmentData.intakeLastName)
          if (enrollmentData.intakeEmail) {
            updateEnrollmentData('injuredPartyEmail', enrollmentData.intakeEmail)
          }
        }
      }
    }
    // ✅ CRITICAL: Only depend on relationToVictim, NOT on firstName/lastName
    // This prevents the effect from running on every keystroke
  }, [enrollmentData.relationToVictim, updateEnrollmentData])
  
  // ✅ Memoize onChange handlers to prevent unnecessary re-renders
  const handleFirstNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    updateEnrollmentData('intakeFirstName', value)
    // Only sync immediately if relation is already set (prevents focus loss)
    if (enrollmentData.relationToVictim === 'Myself') {
      updateEnrollmentData('injuredPartyFirstName', value)
    } else if (enrollmentData.relationToVictim === 'Loved one') {
      updateEnrollmentData('callerFirstName', value)
    }
  }, [enrollmentData.relationToVictim, updateEnrollmentData])

  const handleLastNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    updateEnrollmentData('intakeLastName', value)
    
    // Only sync immediately if relation is already set
    if (enrollmentData.relationToVictim === 'Myself') {
      updateEnrollmentData('injuredPartyLastName', value)
    } else if (enrollmentData.relationToVictim === 'Loved one') {
      updateEnrollmentData('callerLastName', value)
    }
  }, [enrollmentData.relationToVictim, updateEnrollmentData])

  // Auto-populate minor status based on relation to victim
  React.useEffect(() => {
    if (enrollmentData.relationToVictim) {
      if (enrollmentData.relationToVictim === 'Loved one') {
        // Auto-populate minor status as "Yes" for loved one cases
        if (enrollmentData.isMinor === undefined || enrollmentData.isMinor === null) {
          updateEnrollmentData('isMinor', true)
        }
      } else if (enrollmentData.relationToVictim === 'Myself') {
        // Auto-populate minor status as "No" for myself cases (minors cannot enroll themselves)
        // If user manually selects "Yes" for minor when "Myself" is selected, validation will catch it
        // and show error message - we don't auto-reset it so user can see the error
        if (enrollmentData.isMinor === undefined || enrollmentData.isMinor === null) {
          updateEnrollmentData('isMinor', false)
        }
        // Note: We don't auto-reset if user manually sets isMinor to true - let validation show the error
      }
    }
  }, [enrollmentData.relationToVictim, enrollmentData.isMinor, updateEnrollmentData])
  
  // Auto-populate verbal permission as "Verbal permission given" when minor is selected
  React.useEffect(() => {
    if (enrollmentData.isMinor === true) {
      // Set to "Verbal permission given" by default and keep it read-only
      if (!enrollmentData.verbalPermission || enrollmentData.verbalPermission !== 'Verbal Permission given') {
        updateEnrollmentData('verbalPermission', 'Verbal Permission given')
      }
    }
  }, [enrollmentData.isMinor, enrollmentData.verbalPermission, updateEnrollmentData])

  // Sync legal authorization for loved one flows so Step 3 validation passes
  React.useEffect(() => {
    if (enrollmentData.relationToVictim === 'Loved one') {
      if (enrollmentData.isMinor === true) {
        if (enrollmentData.hasLegalAuthorization !== true) {
          updateEnrollmentData('hasLegalAuthorization', true)
        }
      } else if (enrollmentData.isMinor === false) {
        const authorizationGranted = legalGuardianConfirmed === true
        if (enrollmentData.hasLegalAuthorization !== authorizationGranted) {
          updateEnrollmentData('hasLegalAuthorization', authorizationGranted)
        }
      }
    } else if (enrollmentData.relationToVictim === 'Myself') {
      if (enrollmentData.hasLegalAuthorization !== true) {
        updateEnrollmentData('hasLegalAuthorization', true)
      }
    } else if (enrollmentData.hasLegalAuthorization !== undefined && enrollmentData.hasLegalAuthorization !== null) {
      updateEnrollmentData('hasLegalAuthorization', undefined)
    }
  }, [
    enrollmentData.relationToVictim,
    enrollmentData.isMinor,
    enrollmentData.hasLegalAuthorization,
    legalGuardianConfirmed,
    updateEnrollmentData
  ])

  // Auto-populate fields to avoid duplicates in later steps
  React.useEffect(() => {
    // Sync intakeDOB to injuredPartyDOB (used in Step 2) - Always sync when intakeDOB changes
    if (enrollmentData.intakeDOB) {
      // Always update injuredPartyDOB to match intakeDOB (Step 1 takes precedence)
      if (enrollmentData.injuredPartyDOB !== enrollmentData.intakeDOB) {
        updateEnrollmentData('injuredPartyDOB', enrollmentData.intakeDOB)
      }
    }
    
    // Sync intakeState to injuredPartyResidentState (used in Step 3)
    if (enrollmentData.intakeState && !enrollmentData.injuredPartyResidentState) {
      updateEnrollmentData('injuredPartyResidentState', enrollmentData.intakeState)
    }
    
    // Sync robloxGamerTag to robloxUsername if available (used in Step 3)
    if (enrollmentData.robloxGamerTag && !enrollmentData.robloxUsername) {
      updateEnrollmentData('robloxUsername', enrollmentData.robloxGamerTag)
    }
  }, [enrollmentData.intakeDOB, enrollmentData.injuredPartyDOB, enrollmentData.exploitationStartDate, enrollmentData.intakeState, enrollmentData.injuredPartyResidentState, enrollmentData.robloxGamerTag, enrollmentData.robloxUsername, updateEnrollmentData])

  // Reverse auto-population: if later steps have data, populate Step 1 fields
  React.useEffect(() => {
    // If injuredPartyDOB exists but intakeDOB doesn't, populate it
    if (enrollmentData.injuredPartyDOB && !enrollmentData.intakeDOB) {
      updateEnrollmentData('intakeDOB', enrollmentData.injuredPartyDOB)
    }
    
    
    // If injuredPartyResidentState exists but intakeState doesn't, populate it
    if (enrollmentData.injuredPartyResidentState && !enrollmentData.intakeState) {
      updateEnrollmentData('intakeState', enrollmentData.injuredPartyResidentState)
    }
    
    // If robloxUsername exists but robloxGamerTag doesn't, populate it
    if (enrollmentData.robloxUsername && !enrollmentData.robloxGamerTag) {
      updateEnrollmentData('robloxGamerTag', enrollmentData.robloxUsername)
    }
    
    // Populate the input field if we have the gamer tag
    if (enrollmentData.robloxGamerTag && !robloxGamerTag) {
      setRobloxGamerTag(enrollmentData.robloxGamerTag)
    } else if (enrollmentData.robloxUsername && !robloxGamerTag) {
      setRobloxGamerTag(enrollmentData.robloxUsername)
    }
  }, [enrollmentData.injuredPartyDOB, enrollmentData.intakeDOB, enrollmentData.exploitationStartDate, enrollmentData.injuredPartyResidentState, enrollmentData.intakeState, enrollmentData.robloxUsername, enrollmentData.robloxGamerTag, robloxGamerTag, updateEnrollmentData])

  // Reset legal guardian confirmation when conditions change
  React.useEffect(() => {
    if (enrollmentData.relationToVictim !== 'Loved one' || enrollmentData.isMinor !== false) {
      setLegalGuardianConfirmed(false)
    }
  }, [enrollmentData.relationToVictim, enrollmentData.isMinor])

  const handleNext = useCallback(async (): Promise<boolean> => {
    const validationErrors = validateRobloxIntakeContact(enrollmentData, legalGuardianConfirmed)
    setErrors(validationErrors)

    const isValid = Object.keys(validationErrors).length === 0

    if (!isValid) {
      // Scroll to first error field and highlight all errors
      handleValidationErrors(validationErrors)
      return false
    }

    return true
  }, [enrollmentData, legalGuardianConfirmed])

  const getValidationErrors = (): string[] => {
    return Object.values(errors).filter(Boolean)
  }

  const handleBack = useCallback(() => {
    setErrors({})

    // ✅ Since this is the first step (Step 0), go back to campaign selection
    // Using the onCampaignSelect callback for proper state management
    if (onCampaignSelect) {
      onCampaignSelect(null)
    } else {
      console.warn('⚠️ onCampaignSelect not available')
    }
  }, [onCampaignSelect])

  const handleReportProblem = useCallback((problemType: string, isReported: boolean) => {
    setReportedProblems(prev => ({ ...prev, [problemType]: isReported }))
  }, [])

  const handleCheckRobloxGamerTag = async () => {
    if (!robloxGamerTag.trim()) {
      alert('Please enter a Roblox gamer tag first')
      return
    }

    setIsCheckingRobloxTag(true)
    setRobloxGamerTagResult(null)

    try {
      const result = await GamerTagService.checkGamerTag(robloxGamerTag.trim(), 'roblox')
      setRobloxGamerTagResult(result)
      
      if (result.pass) {
        // Save to both fields to avoid duplicates in later steps
        updateEnrollmentData('robloxGamerTag', result.gamer_tag)
        updateEnrollmentData('robloxUsername', result.gamer_tag)
      }
    } catch (error: any) {
      console.error('Error checking Roblox gamer tag:', error)
      
      const errorResult: GamerTagResult = {
        gamer_tag: robloxGamerTag,
        totalHoursPlayed: 0,
        pass: false,
        error: error.response?.data?.error || 'API Error',
        details: error.response?.data?.details || 'Gamer Tag not found or is private'
      }
      setRobloxGamerTagResult(errorResult)
    } finally {
      setIsCheckingRobloxTag(false)
    }
  }

  return (
    <StageWrapper
      currentStepIndex={currentStepIndex ?? 0}
      totalSteps={totalSteps}
      handleNavigate={handleNavigate}
      handleNavigateWithCompletion={handleNavigateWithCompletion}
      onNext={handleNext}
      onBack={handleBack}
      isNavigating={isNavigating}
      showBackButton={false}
    >
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
            <User className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">Roblox Intake Contact</h3>
            <p className="text-sm text-accent-600">Provide initial contact and relationship information.</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Contact Information */}
          <div>
            <h4 className="text-md font-semibold text-accent-800 mb-4">Contact Information</h4>
          {/* Basic intake fields - simplified for Roblox */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  key="intakeFirstName"
                  type="text"
                  value={enrollmentData.intakeFirstName || ''}
                  onChange={handleFirstNameChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.intakeFirstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.intakeFirstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.intakeFirstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  key="intakeLastName"
                  type="text"
                  value={enrollmentData.intakeLastName || ''}
                  onChange={handleLastNameChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.intakeLastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.intakeLastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.intakeLastName}</p>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={enrollmentData.intakeEmail || ''}
                onChange={(e) => {
                  updateEnrollmentData('intakeEmail', e.target.value)
                  updateEnrollmentData('clientEmail', e.target.value) // Also save as clientEmail for backend
                  
                  // If relation is "Myself", also set injuredPartyEmail
                  if (enrollmentData.relationToVictim === 'Myself') {
                    updateEnrollmentData('injuredPartyEmail', e.target.value)
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.intakeEmail ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.intakeEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.intakeEmail}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                value={enrollmentData.intakePhone || ''}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  updateEnrollmentData('intakePhone', formatted);
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.intakePhone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="(123) 456-7890"
                maxLength={14}
              />
              {errors.intakePhone && (
                <p className="mt-1 text-sm text-red-600">{errors.intakePhone}</p>
              )}
            </div>
          </div>
          </div>

          {/* Relationship to Injured Party */}
          <div>
            <h4 className="text-md font-semibold text-accent-800 mb-4">Relationship Information</h4>
            <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Are you calling for yourself or on behalf of your loved one? *
              </label>
              <select
                value={enrollmentData.relationToVictim || ''}
                onChange={(e) => updateEnrollmentData('relationToVictim', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.relationToVictim ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Myself">Myself</option>
                <option value="Loved one">Loved one</option>
              </select>
              {errors.relationToVictim && (
                <p className="mt-1 text-sm text-red-600">{errors.relationToVictim}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {enrollmentData.relationToVictim === 'Myself' 
                  ? 'Are you a minor (under age of 18)?' 
                  : 'Injured Party is Minor?'
                } *
              </label>
              <select
                value={enrollmentData.isMinor === undefined ? '' : enrollmentData.isMinor ? 'Yes' : 'No'}
                onChange={(e) => updateEnrollmentData('isMinor', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.isMinor ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              
              {/* DQ Error for "Myself" with minor status "Yes" */}
              {enrollmentData.relationToVictim === 'Myself' && enrollmentData.isMinor === true && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION: Minors can not enroll themselves. Please select "Loved one" if calling on behalf of a minor.
                  </p>
                </div>
              )}
              
              {/* Warning for "Myself" with minor status "No" */}
              {enrollmentData.relationToVictim === 'Myself' && enrollmentData.isMinor === false && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ Warning: You need to be a minor at the time of incident and the incident must have happened within 2 years maximum.
                  </p>
                </div>
              )}
              
              {errors.isMinor && (
                <p className="mt-1 text-sm text-red-600">{errors.isMinor}</p>
              )}
            </div>

            {/* Conditional fields for minors */}
            {enrollmentData.isMinor && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Could I get verbal permission to speak to you on their behalf? *
                  </label>
                  <select
                    value={enrollmentData.verbalPermission || 'Verbal Permission given'}
                    disabled
                    className={`w-full px-3 py-2 border rounded-md bg-gray-100 cursor-not-allowed ${
                      errors.verbalPermission ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="Verbal Permission given">Verbal Permission given</option>
                    <option value="PC to call back with verbal permission">PC to call back with verbal permission</option>
                    <option value="No verbal permission">No verbal permission</option>
                  </select>
                  {errors.verbalPermission && (
                    <p className="mt-1 text-sm text-red-600">{errors.verbalPermission}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    This field is automatically set to "Verbal Permission given" and cannot be edited
                  </p>
                  <p className="mt-1 text-xs text-red-600">
                    If Verbal permission not given at the time of intake DQ until Verbal permission given
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Relationship to the minor *
                  </label>
                  <select
                    value={enrollmentData.relationshipToMinor || ''}
                    onChange={(e) => updateEnrollmentData('relationshipToMinor', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.relationshipToMinor ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select an option</option>
                    <option value="Parent/Guardian">Parent/Guardian</option>
                    <option value="Custody of the child">Custody of the child</option>
                    <option value="Documented authority to speak on behalf of the Injured Party">Documented authority to speak on behalf of the Injured Party</option>
                    <option value="None">None</option>
                  </select>
                  {errors.relationshipToMinor && (
                    <p className="mt-1 text-sm text-red-600">{errors.relationshipToMinor}</p>
                  )}
                  <p className="mt-1 text-xs text-red-600">
                    IF NONE - DQ
                  </p>
                </div>
              </>
            )}
            </div>
          </div>

          {/* Eligibility Screening */}
          <div>
            <h4 className="text-md font-semibold text-accent-800 mb-4">Eligibility Screening</h4>
            
            {/* State Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select State *
              </label>
              <select
                value={enrollmentData.intakeState || enrollmentData.injuredPartyResidentState || ''}
                onChange={(e) => {
                  updateEnrollmentData('intakeState', e.target.value)
                  // Also sync to injuredPartyResidentState for Step 3
                  updateEnrollmentData('injuredPartyResidentState', e.target.value)
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.intakeState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select a state</option>
                {US_STATES.filter(state => state.value !== '').map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.intakeState && (
                <p className="mt-1 text-sm text-red-600">{errors.intakeState}</p>
              )}
              {(enrollmentData.intakeState?.toLowerCase() === 'florida' || enrollmentData.injuredPartyResidentState?.toLowerCase() === 'florida') && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION: Florida residents are not eligible for this campaign.
                  </p>
                </div>
              )}
            </div>

            {/* Date of Birth of Gamer */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Date of birth of Gamer *
              </label>
              <input
                type="date"
                value={enrollmentData.intakeDOB || enrollmentData.injuredPartyDOB || ''}
                onChange={(e) => {
                  updateEnrollmentData('intakeDOB', e.target.value)
                  // Also sync to injuredPartyDOB for Step 2
                  updateEnrollmentData('injuredPartyDOB', e.target.value)
                  // If relation is "Myself", also set injuredPartyDOB
                  if (enrollmentData.relationToVictim === 'Myself') {
                    updateEnrollmentData('injuredPartyDOB', e.target.value)
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.intakeDOB ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.intakeDOB && (
                <p className="mt-1 text-sm text-red-600">{errors.intakeDOB}</p>
              )}
              
              {/* ✅ Minor Age Restriction Warning - Show when date indicates minor but user selected "No" */}
              {enrollmentData.intakeDOB && enrollmentData.isMinor === false && (() => {
                // Calculate age from date of birth
                const birthDate = new Date(enrollmentData.intakeDOB)
                const today = new Date()
                let age = today.getFullYear() - birthDate.getFullYear()
                const monthDiff = today.getMonth() - birthDate.getMonth()
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                  age--
                }
                
                // Show warning if age is less than 18
                if (age < 18) {
                  return (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600 text-lg">⚠️</span>
                        <h4 className="font-medium text-red-800">Minor Age Restriction</h4>
                      </div>
                      <p className="text-red-700 text-sm mt-1">
                        The gamer is {age} years old. A parent or legal guardian must be present and must sign up the gamer because the gamer is a minor under the age of 18.
                      </p>
                    </div>
                  )
                }
                return null
              })()}
            </div>

            {/* Did incident happen less than 2 years ago */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did incident happen less than 2 years ago? *
              </label>
              <select
                value={enrollmentData.incidentHappenedLessThan2Years === undefined ? '' : enrollmentData.incidentHappenedLessThan2Years ? 'Yes' : 'No'}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    updateEnrollmentData('incidentHappenedLessThan2Years', undefined);
                  } else {
                    updateEnrollmentData('incidentHappenedLessThan2Years', value === 'Yes');
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.incidentHappenedLessThan2Years ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.incidentHappenedLessThan2Years && (
                <p className="mt-1 text-sm text-red-600">{errors.incidentHappenedLessThan2Years}</p>
              )}
              {!errors.incidentHappenedLessThan2Years && enrollmentData.incidentHappenedLessThan2Years === false && (
                <p className="mt-1 text-xs text-red-600">
                  ⚠️ DISQUALIFICATION: Incident happened more than 2 years ago - not eligible
                </p>
              )}
            </div>

            {/* Do you know Roblox gamer tag */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you know Roblox gamer tag? *
              </label>
              <select
                value={enrollmentData.knowsRobloxGamerTag === undefined ? '' : enrollmentData.knowsRobloxGamerTag ? 'Yes' : 'No'}
                onChange={(e) => updateEnrollmentData('knowsRobloxGamerTag', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.knowsRobloxGamerTag ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.knowsRobloxGamerTag && (
                <p className="mt-1 text-sm text-red-600">{errors.knowsRobloxGamerTag}</p>
              )}
            </div>
          </div>

          {/* Legal Guardian Confirmation for Adult Loved Ones */}
          {enrollmentData.relationToVictim === 'Loved one' && enrollmentData.isMinor === false && (
            <div>
              <h4 className="text-md font-semibold text-accent-800 mb-4">Legal Guardian Confirmation</h4>
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium mb-3">
                    ⚠️ Important: You are calling on behalf of an adult (18+ years old).
                  </p>
                  <p className="text-sm text-yellow-800 mb-3">
                    To proceed with this case, you must be the legal guardian or have documented authority to represent this adult.
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="legalGuardianConfirmation"
                    checked={legalGuardianConfirmed}
                    onChange={(e) => setLegalGuardianConfirmed(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="legalGuardianConfirmation" className="text-sm text-gray-700">
                    <span className="font-medium">I confirm that I am the legal guardian</span> of this adult or have documented authority to represent them in legal matters.
                  </label>
                </div>
                
                {errors.legalGuardianRequired && (
                  <p className="text-sm text-red-600">{errors.legalGuardianRequired}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Roblox Gamer Tag Checker (at bottom) */}
      {enrollmentData.knowsRobloxGamerTag === true && (
        <div className="card">
          <div className="mb-4">
            <h4 className="text-md font-semibold text-accent-800 mb-2">Roblox Gamer Tag Checker</h4>
            <p className="text-sm text-gray-600 mb-4">Enter the Roblox gamer tag to verify</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Roblox Gamer Tag *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={robloxGamerTag}
                    onChange={(e) => setRobloxGamerTag(e.target.value)}
                    placeholder="Enter Roblox gamer tag"
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.robloxGamerTag ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleCheckRobloxGamerTag}
                    disabled={isCheckingRobloxTag || !robloxGamerTag.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isCheckingRobloxTag ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Checking...</span>
                      </>
                    ) : (
                      <span>Check</span>
                    )}
                  </button>
                </div>
                {errors.robloxGamerTag && (
                  <p className="mt-1 text-sm text-red-600">{errors.robloxGamerTag}</p>
                )}
              </div>

              {/* Gamer Tag Results */}
              {robloxGamerTagResult && (
                <div className={`p-4 rounded-lg border ${
                  robloxGamerTagResult.pass 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-700">Gamer Tag:</span>
                      <span className="text-sm font-semibold">{robloxGamerTagResult.gamer_tag}</span>
                    </div>
                    
                    {'error' in robloxGamerTagResult && robloxGamerTagResult.error ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Error:</span>
                          <span className="text-sm font-medium text-red-600">{robloxGamerTagResult.error}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Details:</span>
                          <span className="text-sm text-red-600">{robloxGamerTagResult.details || 'Gamer Tag not found or is private'}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Hours Played:</span>
                          <span className="text-sm font-medium">{robloxGamerTagResult.totalHoursPlayed.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Status:</span>
                          <span className={`text-sm font-semibold ${
                            robloxGamerTagResult.pass ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {robloxGamerTagResult.pass ? 'PASS' : 'FAIL'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Validation Error Display */}
      <ValidationErrorDisplay
        errors={getValidationErrors()}
        title="Please resolve the following issues to continue:"
      />
    </StageWrapper>
  )
}

